import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, Building, Clock, MapPin, Star, CheckCircle, Users, BookOpen, TrendingUp, Calendar, Phone, Mail, Languages, DollarSign } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { isAuthenticated, getUserRole } from '../utils/auth';
import ConsultationRequestDialog from '../components/ConsultationRequestDialog';

export default function DoctorDetail() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDoctorDetails();
  }, [doctorId]);

  const fetchDoctorDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/doctors/${doctorId}`);
      
      if (!response.ok) {
        throw new Error('Doctor not found');
      }
      
      const data = await response.json();
      setDoctor(data.doctor);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getHospitalSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const handleBookConsultation = () => {
    setConsultOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center" data-testid="loading-state">
          <div className="text-6xl mb-4">👨‍⚕️</div>
          <p className="text-lg text-gray-600">Loading doctor profile...</p>
        </div>
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center" data-testid="error-state">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Doctor Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The doctor you\'re looking for doesn\'t exist.'}</p>
          <Button onClick={() => navigate('/doctors')} data-testid="back-to-doctors-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Doctors
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="doctor-detail-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-orange-100 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🩹</span>
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                MedRahi
              </h1>
              <p className="text-xs text-gray-600">Healthcare Without Borders</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            onClick={() => navigate('/doctors')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Doctors
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 via-blue-50 to-cyan-50" data-testid="hero-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start gap-6 mb-6">
                <div className="w-32 h-32 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-16 h-16 text-white" />
                </div>
                <div className="flex-1">
                  <h1 className="font-heading text-4xl font-bold text-gray-900 mb-2" data-testid="doctor-name">
                    {doctor.full_name}
                  </h1>
                  <p className="text-xl text-gray-700 mb-3" data-testid="doctor-specialization">
                    {doctor.specialization}
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      <span className="font-semibold text-lg" data-testid="doctor-rating">{doctor.rating}</span>
                      <span className="text-gray-600 text-sm">({doctor.total_patients}+ patients)</span>
                    </div>
                    <Badge className="bg-green-100 text-green-800">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      {doctor.success_rate}% Success Rate
                    </Badge>
                    {doctor.verified && (
                      <Badge className="bg-blue-100 text-blue-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-4" data-testid="doctor-about">
                    {doctor.about || 'Experienced medical professional dedicated to providing excellent patient care.'}
                  </p>

                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{doctor.experience_years} years of experience</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="lg:col-span-1">
              <Card className="border-2 border-teal-200 shadow-lg sticky top-24" data-testid="quick-actions-card">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">Consultation Info</h3>
                  
                  {doctor.consultation_fee && (
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Consultation Fee</span>
                      <span className="font-semibold text-green-600 text-lg" data-testid="consultation-fee">
                        ${doctor.consultation_fee}
                      </span>
                    </div>
                  )}
                  
                  {doctor.consultation_timings && (
                    <div className="py-3 border-b border-gray-100">
                      <p className="text-sm text-gray-600 mb-1">Available Timings</p>
                      <p className="font-semibold text-gray-900" data-testid="consultation-timings">
                        {doctor.consultation_timings}
                      </p>
                    </div>
                  )}
                  
                  {doctor.availability && doctor.availability.length > 0 && (
                    <div className="py-3 border-b border-gray-100">
                      <p className="text-sm text-gray-600 mb-2">Available Days</p>
                      <div className="flex flex-wrap gap-2">
                        {doctor.availability.map((day, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {day}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {doctor.emergency_available && (
                    <div className="py-3">
                      <Badge className="bg-red-100 text-red-800 w-full justify-center">
                        🚨 Emergency Consultations Available
                      </Badge>
                    </div>
                  )}
                  
                  <Button
                    className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-semibold py-6"
                    onClick={handleBookConsultation}
                    data-testid="book-consultation-btn"
                  >
                    Book Consultation
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications & Education */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="qualifications-section">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">Education & Qualifications</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Qualifications */}
            <Card className="border-2 border-teal-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-6 h-6 text-teal-600" />
                  <h3 className="font-semibold text-lg text-gray-900">Degrees & Certifications</h3>
                </div>
                <div className="space-y-3">
                  {doctor.qualifications && doctor.qualifications.map((qual, idx) => (
                    <div key={idx} className="flex items-start gap-2" data-testid={`qualification-${idx}`}>
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{qual}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Medical School */}
            <Card className="border-2 border-teal-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-6 h-6 text-teal-600" />
                  <h3 className="font-semibold text-lg text-gray-900">Medical Education</h3>
                </div>
                {doctor.medical_school && (
                  <p className="text-gray-700 mb-4" data-testid="medical-school">{doctor.medical_school}</p>
                )}
                {doctor.certifications && doctor.certifications.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Additional Certifications:</p>
                    <div className="space-y-2">
                      {doctor.certifications.map((cert, idx) => (
                        <Badge key={idx} variant="outline" className="mr-2 mb-2" data-testid={`certification-${idx}`}>
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Specializations */}
      {doctor.sub_specializations && doctor.sub_specializations.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="specializations-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">Areas of Expertise</h2>
            <div className="flex flex-wrap gap-3">
              {doctor.sub_specializations.map((spec, idx) => (
                <Badge
                  key={idx}
                  className="px-4 py-2 text-base bg-teal-100 text-teal-800 border-teal-200"
                  data-testid={`subspecialization-${idx}`}
                >
                  {spec}
                </Badge>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hospital Affiliations */}
      {doctor.hospital_affiliations && doctor.hospital_affiliations.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="hospitals-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">
              Hospital Affiliations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {doctor.hospital_affiliations.map((hospital, idx) => (
                <Card
                  key={idx}
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/hospitals/${getHospitalSlug(hospital)}`)}
                  data-testid={`hospital-card-${idx}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{hospital}</h3>
                        <Button
                          variant="link"
                          className="p-0 h-auto text-teal-600 hover:text-teal-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/hospitals/${getHospitalSlug(hospital)}`);
                          }}
                        >
                          View Hospital Details →
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Professional Achievements */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="achievements-section">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-gray-900 mb-12 text-center">
            Professional Achievements
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center border-2 border-teal-200">
              <CardContent className="p-6">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-blue-600 mb-2" data-testid="stat-patients">
                  {doctor.total_patients}+
                </div>
                <p className="text-gray-600 font-medium">Patients Treated</p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 border-teal-200">
              <CardContent className="p-6">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-blue-600 mb-2" data-testid="stat-experience">
                  {doctor.experience_years}
                </div>
                <p className="text-gray-600 font-medium">Years Experience</p>
              </CardContent>
            </Card>

            {doctor.publications > 0 && (
              <Card className="text-center border-2 border-teal-200">
                <CardContent className="p-6">
                  <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-blue-600 mb-2" data-testid="stat-publications">
                    {doctor.publications}
                  </div>
                  <p className="text-gray-600 font-medium">Research Publications</p>
                </CardContent>
              </Card>
            )}

            <Card className="text-center border-2 border-teal-200">
              <CardContent className="p-6">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-blue-600 mb-2" data-testid="stat-success-rate">
                  {doctor.success_rate}%
                </div>
                <p className="text-gray-600 font-medium">Success Rate</p>
              </CardContent>
            </Card>
          </div>

          {/* Awards */}
          {doctor.awards && doctor.awards.length > 0 && (
            <div className="mt-12">
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-6 text-center">Awards & Recognition</h3>
              <div className="flex flex-wrap justify-center gap-4">
                {doctor.awards.map((award, idx) => (
                  <Card key={idx} className="border-2 border-yellow-200 bg-yellow-50" data-testid={`award-${idx}`}>
                    <CardContent className="p-4 flex items-center gap-2">
                      <Award className="w-5 h-5 text-yellow-600" />
                      <span className="font-medium text-gray-800">{award}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Research Interests */}
      {doctor.research_interests && doctor.research_interests.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="research-section">
          <div className="mx-auto max-w-5xl">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8 text-center">
              Research Interests
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {doctor.research_interests.map((interest, idx) => (
                <Card key={idx} className="border-2 border-teal-100" data-testid={`research-interest-${idx}`}>
                  <CardContent className="p-4 text-center">
                    <BookOpen className="w-6 h-6 text-teal-600 mx-auto mb-2" />
                    <p className="font-medium text-gray-800">{interest}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Languages */}
      {doctor.languages && doctor.languages.length > 0 && (
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="languages-section">
          <div className="mx-auto max-w-4xl text-center">
            <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">Languages Spoken</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {doctor.languages.map((language, idx) => (
                <Badge
                  key={idx}
                  variant="outline"
                  className="px-4 py-2 text-base"
                  data-testid={`language-${idx}`}
                >
                  <Languages className="w-4 h-4 mr-2" />
                  {language}
                </Badge>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-500 to-blue-600 text-white" data-testid="cta-section">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Ready to Consult with {doctor.full_name}?
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Book your consultation today and get expert medical care from an experienced specialist
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-white text-teal-600 hover:bg-gray-100 font-semibold text-lg px-8 py-6"
              onClick={handleBookConsultation}
              data-testid="cta-book-consultation-btn"
            >
              Book Consultation Now
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 font-semibold text-lg px-8 py-6"
              onClick={() => navigate('/doctors')}
              data-testid="explore-doctors-btn"
            >
              Explore More Doctors
            </Button>
          </div>
        </div>
      </section>
      <ConsultationRequestDialog
        open={consultOpen}
        onOpenChange={setConsultOpen}
        context={{ type: 'doctor', name: doctor.full_name, treatmentCode: 'other' }}
      />
    </div>
  );
}
